/**
 * Runtime SPA configuration (spec 20260701-runtime-spa-config-build-once-promote-many).
 *
 * Build once, promote many: a single artifact resolves its environment-specific config at
 * runtime from a SAME-ORIGIN static file (`/runtime-config.json`), generated by the web
 * container's entrypoint from that environment's app settings at startup - instead of baking
 * `VITE_*` at build time. Same-origin matters: the bootstrap needs only the origin the browser
 * already provides (the SPA reaches the function app cross-origin via functionAppUrl, which is
 * IN this config, so config cannot live on the function app). `loadRuntimeConfig()` MUST be
 * awaited before any module that reads config initialises (see index.tsx).
 *
 * In a deployed build the endpoint is authoritative and there is NO fallback to build-time
 * values (fail-closed). In local dev (`import.meta.env.DEV`) it falls back to `.env` `VITE_*`
 * so `npm run dev` keeps working without the Functions host.
 */
import { isViteDev, viteFallbackConfig } from "./viteDevEnv";

export interface RuntimeConfig {
  functionAppUrl: string;
  conversationStreamApiBase: string;
  entraTenantId: string;
  entraClientId: string;
  entraAuthority: string;
  stripePublishableKey: string;
  recaptchaSiteKey: string;
  // Self-serve signup gate. Closed by default so customer deployments hide the /signup routes;
  // Cordexa dev/uat/prod set it true (spec 20260703-gate-self-serve-signup).
  signupEnabled: boolean;
}

const EMPTY: RuntimeConfig = {
  functionAppUrl: "",
  conversationStreamApiBase: "",
  entraTenantId: "",
  entraClientId: "",
  entraAuthority: "",
  stripePublishableKey: "",
  recaptchaSiteKey: "",
  signupEnabled: false,
};

/**
 * Test-environment bootstrap seam. Jest has no equivalent of `index.tsx` (which awaits
 * `loadRuntimeConfig()` before importing anything that reads config), so module-load-time
 * readers - `src/config.ts` and `src/api/api.ts` both call `getRuntimeConfig()` while
 * evaluating - would throw "Runtime config not loaded" in every suite. The Jest setup file
 * `jest/runtimeConfigBootstrap.cjs` defines this global before any module loads; it is
 * seeded here rather than by an exported setter so the setup file never has to `require`
 * this module (doing so cached it registry-wide and silently defeated `jest.mock()` of its
 * dependencies in runtimeConfig.test.ts). In a browser the global never exists, so this is
 * exactly `null` and every deployed code path is unchanged.
 */
const _testSeed = (globalThis as { __RUNTIME_CONFIG_TEST_SEED__?: RuntimeConfig })
  .__RUNTIME_CONFIG_TEST_SEED__;

let _config: RuntimeConfig | null = _testSeed ? { ...EMPTY, ..._testSeed } : null;

/**
 * Fetch and cache the runtime config. Safe to call more than once (returns the cached copy).
 * Throws in a deployed build when the endpoint is unavailable (fail-closed); falls back to
 * `.env` only in local dev.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (_config) {
    return _config;
  }
  try {
    // Same-origin static file generated by the web container entrypoint from this environment's
    // app settings (no chicken-and-egg: needs only the origin the browser already provides).
    const resp = await fetch("/runtime-config.json", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
    });
    if (!resp.ok) {
      throw new Error(`runtime-config responded ${resp.status}`);
    }
    const data = (await resp.json()) as Partial<RuntimeConfig>;
    _config = { ...EMPTY, ...data };
    return _config;
  } catch (err) {
    if (isViteDev()) {
      // Local dev: the Functions host may not be running; use .env VITE_* so `npm run dev` works.
      // eslint-disable-next-line no-console
      console.warn("runtime-config unavailable; using local .env VITE_* fallback (dev only):", err);
      _config = viteFallbackConfig();
      return _config;
    }
    // Deployed build: fail closed. Callers (index.tsx) render a safe "config unavailable" state.
    throw err instanceof Error ? err : new Error("runtime-config unavailable");
  }
}

/** Return the loaded config. Throws if `loadRuntimeConfig()` has not resolved yet. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!_config) {
    throw new Error(
      "Runtime config not loaded. loadRuntimeConfig() must be awaited before reading config.",
    );
  }
  return _config;
}

/** Test-only reset. */
export function __resetRuntimeConfigForTests(): void {
  _config = null;
}
